import { IStarChestData, STAR_CHEST_TIER_DATA } from '../Constants/Chests';
import { MathHelper } from './MathHelper';
import { report } from '../report';
import PIXIRenderer from '../System/Render/PIXIRenderer';
import WorldSystem from '../System/World/WorldSystem';
import Game from '../Game';
import TransformComponent from '../Component/Transform';
import Point from './Point';
import { IElementLayout } from '../Prototype/UI/Element';
import { IGameState } from '../State/Game/IGameState';
import CrossPromoMenuButton from '../Prefabs/UI/HUD/CrossPromoMenuButton';
import { WorldDataHelper } from '../Utility/WorldDataHelper';

export enum StarChestEvent {
    UPGRADE_CHEST = 'UpgradeLevelNodeStarChest',
    REWARD_STAR = 'RewardStarToStarChest',
}

export class StarChestHelper {
    /**
     * Checks if the provided tier exists
     */
    public static tierExists(tier: number): boolean {
        return MathHelper.isWithinRange(tier, 0, STAR_CHEST_TIER_DATA.length - 1);
    }
    /**
     * Get the normal star chest asset for the tier supplied
     */
    public static getStarChestTexture(tier: number) {
        return `star_chest_${tier}`;
    }
    /**
     * Get the progress bar star chest asset for the tier supplied
     */
    public static getStarChestProgressBarTexture(tier: number) {
        return `progressbar_icon${tier}`;
    }
    /**
     * Get the % completed between the current star chest requierments and the next
     * Will always return 1 if at the last tier already
     */
    public static getProgressToNextStarChest(
        currentTier: number,
        starCount: number,
        paddingLeft: number = 0,
        paddingRight: number = 0
    ): number {
        const { from, to } = this.getProgressTierDatas(currentTier);
        if (!from || !to) return 0 + paddingLeft;
        if (starCount >= to.requiredStars) {
            // Not using `paddingRight` intentionally
            return 1;
        }

        return MathHelper.remap(
            starCount,
            from.requiredStars,
            to.requiredStars,
            0 + paddingLeft,
            1 - paddingRight,
            true
        );
    }

    public static getProgressTierDatas(currentTier: number) {
        const nextOrLastTier = this.getNextOrLastTier(currentTier);
        const currentOrPreviousTier = this.getCurrentOrPreviousTier(currentTier);

        const from = this.getChestTierData(currentOrPreviousTier);
        const to = this.getChestTierData(nextOrLastTier);

        return { from, to };
    }
    /**
     * Gets star chest tier data
     */
    public static getChestTierData(tier: number): IStarChestData | null {
        const tierData = STAR_CHEST_TIER_DATA[tier];
        if (!tierData) {
            report('Unexpected star chest tier', {
                tags: {
                    tier,
                },
                once: true,
                sampleRate: 0.1,
            });
            return null;
        }
        return tierData;
    }
    /**
     * Gets the next tier after the tier provided.
     * If the last tier is provided then the last tier will returned
     */
    public static getNextOrLastTier(tier: number): number {
        return Math.min(tier + 1, STAR_CHEST_TIER_DATA.length - 1);
    }
    /**
     * Returns the tier provided unless the last tier is provided,
     * then the previous tier will be returned
     */
    public static getCurrentOrPreviousTier(tier: number): number {
        const nextOrLast = this.getNextOrLastTier(tier);
        return nextOrLast > tier ? tier : nextOrLast - 1;
    }

    public static getStarCountCap(): number {
        const lastTierData = this.getChestTierData(STAR_CHEST_TIER_DATA.length - 1);
        return lastTierData.requiredStars;
    }

    /**
     * Returns the anchor for the future chest bubble,
     * based on the last level node position from world
     * and being or not the last world.
     */
    public static getFutureChestAnchor(
        state: IGameState
    ): NonNullable<IElementLayout['anchors']> {
        // Wheel icon is no longer on map so we can just move this to that spot permanently
        // FIXME: remove old code when safe to
        return [0.93, 0.19, 0.93, 0.19];
    }
}
